import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteIcon from "@mui/icons-material/ContentPaste";
import { toast } from "react-toastify";
import SpecsRevisionService from "../../service/SpecsRevisionService";
import { Input } from "../../common/components/Input";
import { spacing } from "../../styles/theme";
import SpecsFormPage from "./specsForm/SpecsFormPage";
import type { SpecsFormPageSchema } from "./specsForm/types";
import { ProductVersionDecorationTypeModel } from "./specsModel/productMetadata/ProductVersionDecorationTypeModel";
import { ProductVersionFactoryPricingBracketModel } from "./specsModel/productMetadata/ProductVersionFactoryPricingBracketModel";
import { ProductVersionFactoryPricingExtraColorModel } from "./specsModel/productMetadata/ProductVersionFactoryPricingExtraColorModel";
import { ProductVersionFactoryPricingLargeSizeModel } from "./specsModel/productMetadata/ProductVersionFactoryPricingLargeSizeModel";
import { ProductVersionFactoryPricingModel } from "./specsModel/productMetadata/ProductVersionFactoryPricingModel";
import { ProductVersionGeneratorMetadataModel } from "./specsModel/productMetadata/ProductVersionGeneratorMetadataModel";
import { ProductVersionImageSideModel } from "./specsModel/productMetadata/ProductVersionImageSideModel";
import { ProductVersionCompanyLogoLabelModel } from "./specsModel/productMetadata/ProductVersionCompanyLogoLabelModel";
import { ProductVersionLabelColorModel } from "./specsModel/productMetadata/ProductVersionLabelColorModel";
import { ProductVersionLabelModel } from "./specsModel/productMetadata/ProductVersionLabelModel";
import { ProductVersionPackagingModel } from "./specsModel/productMetadata/ProductVersionPackagingModel";
import { ProductVersionSizeModel } from "./specsModel/productMetadata/ProductVersionSizeModel";
import { ProductVersionSizeRecommendationBracketModel } from "./specsModel/productMetadata/ProductVersionSizeRecommendationBracketModel";
import { ProductVersionSizeRecommendationsModel } from "./specsModel/productMetadata/ProductVersionSizeRecommendationsModel";
import { ProductVersionTechpackAnnexeModel } from "./specsModel/productMetadata/ProductVersionTechpackAnnexeModel";
import type {
  SpecsCatalogDecoration,
  SpecsModelSnapshot,
} from "./specsModel/types";
import {
  unifyProductImageDimensions,
  uploadNewGrayscaleAssets,
  uploadRGBAAssets,
} from "./specsProductGrayscaleUploads";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import { useSpecsModelContextValue } from "./SpecsModelContext";
import { useProductVersionMetadataModel } from "./useProductVersionMetadataModel";

const metadataSchema: SpecsFormPageSchema = {
  sections: [
    {
      title: "Catalog Metadata",
      description: "Product-level metadata authored on the version file.",
      columns: 2,
      entries: [
        { kind: "field", path: "skuJameo" },
        { kind: "field", path: "skuPrextra" },
        { kind: "field", path: "productSex" },
        { kind: "field", path: "productLine" },
        { kind: "field", path: "status" },
        { kind: "field", path: "factory" },
      ],
    },
    {
      title: "Packaging",
      description: "Packaging instruction assets and SKU sticker metadata.",
      entries: [
        {
          kind: "object",
          path: "packaging",
          title: "Packaging",
          createValue: () => new ProductVersionPackagingModel(),
          removable: false,
          columns: 2,
          entries: [
            { kind: "field", path: "hangTagInstructionsUrl" },
            { kind: "field", path: "polybagUrl" },
            { kind: "field", path: "polybagWithHangTagUrl" },
            { kind: "field", path: "polybagWithSkuUrl" },
            { kind: "field", path: "skuSticker" },
          ],
        },
      ],
    },
    {
      title: "Techpack",
      description: "Techpack annexes and logo preview behavior.",
      entries: [
        { kind: "field", path: "saveLogoPreview" },
        {
          kind: "array",
          path: "techpackAnnexe",
          title: "Techpack Annexes",
          createItem: () => new ProductVersionTechpackAnnexeModel(),
          getItemLabel: (item, index) => {
            const id =
              item && typeof item === "object" && "id" in item
                ? String((item as { id?: string }).id ?? "")
                : "";
            return id || `Annexe ${index + 1}`;
          },
          columns: 2,
          colSpan: 2,
          entries: [
            { kind: "field", path: "id" },
            { kind: "field", path: "pdfAnnexe" },
          ],
        },
      ],
    },
    {
      title: "Product Options",
      description: "Sizes, image sides, and available decoration types.",
      entries: [
        {
          kind: "array",
          path: "sizes",
          title: "Sizes",
          createItem: () => new ProductVersionSizeModel(),
          getItemLabel: (item, index) => {
            const name =
              item && typeof item === "object" && "name" in item
                ? String((item as { name?: string }).name ?? "")
                : "";
            return name || `Size ${index + 1}`;
          },
          columns: 2,
          entries: [
            { kind: "field", path: "name" },
            { kind: "field", path: "displayLabel" },
          ],
        },
        {
          kind: "array",
          path: "availableImageSides",
          title: "Available Image Sides",
          createItem: () => new ProductVersionImageSideModel(),
          getItemLabel: (item, index) => {
            const name =
              item && typeof item === "object" && "name" in item
                ? String((item as { name?: string }).name ?? "")
                : "";
            return name || `Image Side ${index + 1}`;
          },
          entries: [
            { kind: "field", path: "name" },
            { kind: "field", path: "extra" },
          ],
        },
        {
          kind: "array",
          path: "availableDecorationTypes",
          title: "Available Decoration Types",
          createItem: () => new ProductVersionDecorationTypeModel(),
          getItemLabel: (item, index) => {
            const name =
              item && typeof item === "object" && "name" in item
                ? String((item as { name?: string }).name ?? "")
                : "";
            return name || `Decoration Type ${index + 1}`;
          },
          entries: [
            { kind: "field", path: "name" },
            { kind: "field", path: "extra" },
          ],
        },
      ],
    },
    {
      title: "Labels",
      description: "Size/care label metadata and company logo instructions.",
      entries: [
        {
          kind: "array",
          path: "labels",
          title: "Labels",
          createItem: () => new ProductVersionLabelModel(),
          getItemLabel: (item, index) => {
            const type =
              item && typeof item === "object" && "type" in item
                ? String((item as { type?: string }).type ?? "")
                : "";
            return type || `Label ${index + 1}`;
          },
          columns: 2,
          entries: [
            { kind: "field", path: "type" },
            { kind: "field", path: "typeKey" },
            { kind: "field", path: "positionKey" },
            { kind: "field", path: "descriptionKey" },
            {
              kind: "array",
              path: "color",
              title: "Colors",
              createItem: () => new ProductVersionLabelColorModel(),
              getItemLabel: (_item, index) => `Color ${index + 1}`,
              columns: 2,
              colSpan: 2,
              entries: [
                { kind: "field", path: "colorChart" },
                { kind: "field", path: "colorName" },
                { kind: "field", path: "colorHex" },
                { kind: "field", path: "techpackTextHex" },
                { kind: "field", path: "extra", colSpan: 2 },
              ],
            },
            {
              kind: "object",
              path: "companyLogoLabel",
              title: "Company Logo Label",
              createValue: () => new ProductVersionCompanyLogoLabelModel(),
              columns: 2,
              colSpan: 2,
              entries: [
                { kind: "field", path: "descriptionKey" },
                { kind: "field", path: "x1" },
                { kind: "field", path: "x2" },
                { kind: "field", path: "y" },
                { kind: "field", path: "w" },
                { kind: "field", path: "h" },
                { kind: "field", path: "defaultLabelInstructionUrl" },
                { kind: "field", path: "customLabelInstructionUrl" },
                { kind: "field", path: "customLabelInstructionMaskUrl" },
                { kind: "field", path: "extra", colSpan: 2 },
              ],
            },
            { kind: "field", path: "extra", colSpan: 2 },
          ],
        },
      ],
    },
    {
      title: "Sizing Metadata",
      description: "Recommendations and sizing brackets.",
      entries: [
        {
          kind: "object",
          path: "sizeRecommendations",
          title: "Size Recommendations",
          createValue: () => new ProductVersionSizeRecommendationsModel(),
          removable: false,
          entries: [
            {
              kind: "array",
              path: "sizeBrackets",
              title: "Size Brackets",
              createItem: () =>
                new ProductVersionSizeRecommendationBracketModel(),
              getItemLabel: (_item, index) => `Bracket ${index + 1}`,
              columns: 2,
              entries: [
                { kind: "field", path: "percentage" },
                { kind: "field", path: "size" },
                { kind: "field", path: "extra", colSpan: 2 },
              ],
            },
            { kind: "field", path: "extra" },
          ],
        },
      ],
    },
    {
      title: "Factory Pricing",
      description: "Factory pricing curves, surcharges, and extra-color rules.",
      entries: [
        {
          kind: "object",
          path: "factoryPricing",
          title: "Factory Pricing",
          createValue: () => new ProductVersionFactoryPricingModel(),
          removable: false,
          columns: 2,
          entries: [
            { kind: "field", path: "overPolicy" },
            {
              kind: "array",
              path: "priceBrackets",
              title: "Price Brackets",
              createItem: () => new ProductVersionFactoryPricingBracketModel(),
              getItemLabel: (_item, index) => `Bracket ${index + 1}`,
              columns: 2,
              colSpan: 2,
              entries: [
                { kind: "field", path: "quantityUnder" },
                { kind: "field", path: "price" },
                { kind: "field", path: "extra", colSpan: 2 },
              ],
            },
            {
              kind: "array",
              path: "largeSizeSurcharge",
              title: "Large Size Surcharge",
              createItem: () =>
                new ProductVersionFactoryPricingLargeSizeModel(),
              getItemLabel: (item, index) => {
                const sizeName =
                  item && typeof item === "object" && "sizeName" in item
                    ? String((item as { sizeName?: string }).sizeName ?? "")
                    : "";
                return sizeName || `Surcharge ${index + 1}`;
              },
              columns: 2,
              colSpan: 2,
              entries: [
                { kind: "field", path: "sizeName" },
                { kind: "field", path: "price" },
                { kind: "field", path: "extra", colSpan: 2 },
              ],
            },
            {
              kind: "array",
              path: "extraColorPrice",
              title: "Extra Color Price",
              createItem: () =>
                new ProductVersionFactoryPricingExtraColorModel(),
              getItemLabel: (_item, index) => `Extra Color ${index + 1}`,
              columns: 2,
              colSpan: 2,
              entries: [
                { kind: "field", path: "fabricNames", colSpan: 2 },
                {
                  kind: "array",
                  path: "priceBrackets",
                  title: "Price Brackets",
                  createItem: () =>
                    new ProductVersionFactoryPricingBracketModel(),
                  getItemLabel: (_item, index) => `Bracket ${index + 1}`,
                  columns: 2,
                  colSpan: 2,
                  entries: [
                    { kind: "field", path: "quantityUnder" },
                    { kind: "field", path: "price" },
                    { kind: "field", path: "extra", colSpan: 2 },
                  ],
                },
                { kind: "field", path: "extra", colSpan: 2 },
              ],
            },
            { kind: "field", path: "extra", colSpan: 2 },
          ],
        },
      ],
    },
    {
      title: "Additional Metadata",
      description: "Version metadata outside the core product structure.",
      columns: 2,
      entries: [
        { kind: "field", path: "deliveryTime", colSpan: 2 },
        { kind: "field", path: "pricing", colSpan: 2 },
        {
          kind: "object",
          path: "generator",
          title: "Generator Metadata",
          createValue: () => new ProductVersionGeneratorMetadataModel(),
          removable: false,
          entries: [{ kind: "field", path: "extra" }],
          colSpan: 2,
        },
        { kind: "field", path: "extra", colSpan: 2 },
      ],
    },
  ],
};

function getDecorationTypeName(value: unknown) {
  return typeof value === "string" ? value.trim() : "";
}

function createDecorationTypeOptions(values: string[]) {
  return Array.from(
    new Set(values.map((value) => value.trim()).filter(Boolean))
  )
    .sort()
    .map((value) => ({ label: value, value }));
}

function getCatalogDecorationTypeId(decoration: SpecsCatalogDecoration) {
  return decoration.id ?? "";
}

function getCatalogDecorationTypeIds(decorations: SpecsCatalogDecoration[]) {
  return decorations.map(getCatalogDecorationTypeId);
}

function getImportDecorationTypeNames(
  importSpecs: { decorations: SpecsCatalogDecoration[] } | null
) {
  if (!importSpecs) {
    return [];
  }

  return getCatalogDecorationTypeIds(importSpecs.decorations);
}

export default function SpecsMetadataPage() {
  const {
    editor: {
      identity,
      rawVersionMetadata,
      updateIdentity,
      updateRawVersionMetadata,
    },
    importSpecs,
  } = useSpecsEditorContextValue();
  const { specsModel } = useSpecsModelContextValue();
  const metadataModel = useProductVersionMetadataModel({
    rawVersionMetadata,
    updateRawVersionMetadata,
  });
  const [identityDialogOpen, setIdentityDialogOpen] = React.useState(false);
  const [identitySaving, setIdentitySaving] = React.useState(false);
  const [identityDraft, setIdentityDraft] = React.useState({
    productName: identity.productName,
    productDisplayName: identity.productDisplayName,
    productVersion: identity.productVersion,
  });
  const currentVersion = specsModel?.getVersion(identity.productVersion);
  const versionDebugPath = currentVersion?._debugPath;
  const metadataDebugPath = "window.specs.metadataModel";
  const canChangeIdentity = metadataModel.status === "development";
  const openIdentityDialog = React.useCallback(() => {
    setIdentityDraft({
      productName: identity.productName,
      productDisplayName: identity.productDisplayName,
      productVersion: identity.productVersion,
    });
    setIdentityDialogOpen(true);
  }, [
    identity.productDisplayName,
    identity.productName,
    identity.productVersion,
  ]);
  const closeIdentityDialog = React.useCallback(() => {
    setIdentityDialogOpen(false);
  }, []);
  const saveIdentityDraft = React.useCallback(async () => {
    if (!canChangeIdentity) {
      toast.error("Only development products can change identity");
      return;
    }

    if (!specsModel || !importSpecs) {
      toast.error("Unable to change identity without a loaded specs model");
      return;
    }

    const previousIdentity = {
      productName: identity.productName,
      productDisplayName: identity.productDisplayName,
      productVersion: identity.productVersion,
    };
    const nextIdentity = {
      productName: identityDraft.productName.trim(),
      productDisplayName:
        identityDraft.productDisplayName.trim() ||
        identityDraft.productName.trim(),
      productVersion: identityDraft.productVersion.trim(),
    };

    if (!nextIdentity.productName || !nextIdentity.productVersion) {
      toast.error("Product and version are required");
      return;
    }

    const productNameChanged =
      previousIdentity.productName !== nextIdentity.productName;
    const versionChanged =
      previousIdentity.productVersion !== nextIdentity.productVersion;
    const displayNameChanged =
      previousIdentity.productDisplayName !== nextIdentity.productDisplayName;

    if (!productNameChanged && !versionChanged && !displayNameChanged) {
      setIdentityDialogOpen(false);
      return;
    }

    setIdentitySaving(true);
    let savedNewIdentity = false;
    try {
      updateIdentity(nextIdentity);

      const normalizedAssets = await uploadNewGrayscaleAssets({
        model: specsModel,
        specName: nextIdentity.productName,
      });
      const normalizedRGBAAssets = await uploadRGBAAssets({
        snapshot: normalizedAssets.snapshot,
        model: specsModel,
        specName: nextIdentity.productName,
      });
      const unifiedImageDimensions = await unifyProductImageDimensions({
        snapshot: normalizedRGBAAssets.snapshot,
        specName: nextIdentity.productName,
      });
      const productSnapshot =
        unifiedImageDimensions.snapshot as SpecsModelSnapshot;
      await SpecsRevisionService.saveSpecRevision({
        specType: "product",
        specName: nextIdentity.productName,
        snapshot: productSnapshot,
        sourceRevisionId: null,
        createdBy: "specs-editor",
        changeNote: `Changed identity from ${previousIdentity.productName} ${previousIdentity.productVersion}`,
      });
      savedNewIdentity = true;

      if (productNameChanged) {
        await SpecsRevisionService.deleteSpec(
          "product",
          previousIdentity.productName
        );
      }

      window.location.assign(
        `/specs/${encodeURIComponent(nextIdentity.productName)}/${encodeURIComponent(
          nextIdentity.productVersion
        )}`
      );
    } catch (error) {
      if (!savedNewIdentity) {
        updateIdentity(previousIdentity);
      }
      toast.error(
        error instanceof Error ? error.message : "Failed to change identity"
      );
    } finally {
      setIdentitySaving(false);
    }
  }, [
    canChangeIdentity,
    identity.productDisplayName,
    identity.productName,
    identity.productVersion,
    identityDraft.productDisplayName,
    identityDraft.productName,
    identityDraft.productVersion,
    importSpecs,
    specsModel,
    updateIdentity,
  ]);
  const positionDecorationTypeOptions = React.useMemo(
    () =>
      createDecorationTypeOptions(
        currentVersion?.getPositionDecorationTypeNames() ?? []
      ),
    [currentVersion]
  );
  const availableDecorationTypeOptions = React.useMemo(
    () =>
      createDecorationTypeOptions([
        ...positionDecorationTypeOptions.map((option) => option.value),
        ...metadataModel.availableDecorationTypes.map((entry) => entry.name),
        ...getImportDecorationTypeNames(
          importSpecs as { decorations: SpecsCatalogDecoration[] } | null
        ),
      ]),
    [
      importSpecs,
      metadataModel.availableDecorationTypes,
      positionDecorationTypeOptions,
    ]
  );

  metadataModel.availableDecorationTypes.forEach((entry) => {
    entry.setNameOptions(availableDecorationTypeOptions);
  });

  React.useEffect(() => {
    if (
      metadataModel.availableDecorationTypes.length > 0 ||
      positionDecorationTypeOptions.length === 0
    ) {
      return;
    }

    metadataModel.availableDecorationTypes = positionDecorationTypeOptions.map(
      (option) =>
        (
          new ProductVersionDecorationTypeModel().restore({
            name: option.value,
          }) as ProductVersionDecorationTypeModel
        ).setNameOptions(availableDecorationTypeOptions)
    );
  }, [
    availableDecorationTypeOptions,
    metadataModel,
    positionDecorationTypeOptions,
  ]);

  const copyMetadata = React.useCallback(async () => {
    try {
      await navigator.clipboard.writeText(
        JSON.stringify(metadataModel.save(), null, 2)
      );
      toast.success("Copied metadata to clipboard");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to copy metadata to clipboard"
      );
    }
  }, [metadataModel]);

  const pasteMetadata = React.useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      const parsed = JSON.parse(clipboardText);

      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("Clipboard does not contain metadata");
      }

      metadataModel.restore(parsed);
      toast.success("Pasted metadata from clipboard");
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to paste metadata from clipboard"
      );
    }
  }, [metadataModel]);

  if (!specsModel) {
    return (
      <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
        <Typography variant="h2" sx={{ mb: 3 }}>
          Metadata
        </Typography>
        <Alert severity="info">
          Select a product and version to edit metadata.
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: spacing.groupingDesktop }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h2">Metadata</Typography>
        <Tooltip
          title={
            canChangeIdentity
              ? ""
              : "Only development products can change identity"
          }
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              onClick={openIdentityDialog}
              disabled={!canChangeIdentity || identitySaving}
            >
              Modifier l'identité
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Input
          label="Product"
          debugPath={
            specsModel.product?._debugPath
              ? `${specsModel.product._debugPath}.name`
              : undefined
          }
          noMaxWidth
          value={identity.productName}
          inputProps={{ readOnly: true }}
          placeholder="pompomBeanie"
          fullWidth
        />
        <Input
          label="Display name"
          debugPath={
            specsModel.product?._debugPath
              ? `${specsModel.product._debugPath}.extra.displayName`
              : undefined
          }
          noMaxWidth
          value={identity.productDisplayName}
          inputProps={{ readOnly: true }}
          placeholder="Pompom Beanie"
          fullWidth
        />
        <Input
          label="Version"
          debugPath={versionDebugPath ? `${versionDebugPath}.name` : undefined}
          noMaxWidth
          value={identity.productVersion}
          inputProps={{ readOnly: true }}
          placeholder="1.0"
          fullWidth
        />
        <Input
          label="Pixel to Inch"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          debugPath={
            versionDebugPath ? `${versionDebugPath}.pixelToInch` : undefined
          }
          noMaxWidth
          fullWidth
          value={currentVersion ? String(currentVersion.pixelToInch) : ""}
          onChange={(e) => {
            if (!currentVersion) {
              return;
            }
            const rounded = Math.round(Number(e.target.value));
            currentVersion.pixelToInch = rounded;
          }}
          placeholder="1"
        />
      </Stack>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Tooltip title="Copier les metadata">
          <span>
            <IconButton size="small" onClick={copyMetadata}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Coller les metadata">
          <span>
            <IconButton size="small" onClick={pasteMetadata}>
              <ContentPasteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <SpecsFormPage
        description=""
        emptyMessage="No imported version metadata is available for this session."
        model={metadataModel}
        schema={metadataSchema}
        debugPathPrefix={metadataDebugPath}
        imageUploadContext={{
          specType: "product",
          specName: identity.productName || undefined,
        }}
        clickToEditFields
      />
      <Dialog
        open={identityDialogOpen}
        onClose={closeIdentityDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Modifier l'identité</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Stack spacing={2}>
            <Alert severity="warning">
              Changing product or version identity saves a new revision and
              deletes the previous product after the save succeeds. The new
              product will not keep the previous product history.
            </Alert>
            <Input
              label="Product"
              noMaxWidth
              fullWidth
              value={identityDraft.productName}
              onChange={(event) =>
                setIdentityDraft((current) => ({
                  ...current,
                  productName: event.target.value,
                }))
              }
              placeholder="pompomBeanie"
            />
            <Input
              label="Display name"
              noMaxWidth
              fullWidth
              value={identityDraft.productDisplayName}
              onChange={(event) =>
                setIdentityDraft((current) => ({
                  ...current,
                  productDisplayName: event.target.value,
                }))
              }
              placeholder="Pompom Beanie"
            />
            <Input
              label="Version"
              noMaxWidth
              fullWidth
              value={identityDraft.productVersion}
              onChange={(event) =>
                setIdentityDraft((current) => ({
                  ...current,
                  productVersion: event.target.value,
                }))
              }
              placeholder="1.0"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeIdentityDialog}>Annuler</Button>
          <Button
            variant="contained"
            onClick={saveIdentityDraft}
            disabled={
              identitySaving ||
              !identityDraft.productName.trim() ||
              !identityDraft.productVersion.trim()
            }
          >
            {identitySaving ? "Modification..." : "Modifier l'identité"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
